import { useState } from "react";
import labels from "../../utils/labels";
import LabeledSection from "../UI/LabeledSection";
import Accordion from "../UI/Accordion";
import useDataSelector from "../../hooks/useDataSelector";
import { useQueryParams } from "../../hooks/useQueryParams";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { getGanttChartData } from "../../store/ganttChart/action";
import EmployeeSettingsService from "../../service/employeeSettingsService";

type TeamOption = { id: string; title: string };

// Moves the employee to another team for this plan only. Pondoo-side only (never sent to Nexus);
// the backend re-groups the employee and lets the optimizer treat them as part of the new team.
const EmployeeTeamMove = () => {
  const dispatch = useAppDispatch();
  const queryKey = useQueryParams();
  const { dataToUse } = useDataSelector();
  const { employeeShiftId } = useAppSelector(
    (state) => state.drawerSlice.selectedEmployee!,
  );
  const { teamGroupColorMaps } = useAppSelector(
    (state) => state.ganttChartSlice,
  );
  const [isPending, setIsPending] = useState(false);

  const employee = dataToUse?.employees.find(
    (e) => e.shiftSchedule.idShift === employeeShiftId,
  );
  const teamNames = teamGroupColorMaps?.colorGroupToTeamNumberMap;
  const canMoveTeam =
    !!employee && !!teamNames && Object.keys(teamNames).length > 1;
  if (!canMoveTeam) return null;

  const teamOptions = Object.entries(teamNames).map(([colorGroup, name]) => ({
    id: colorGroup,
    title: name,
  }));
  const currentIndex = teamOptions.findIndex(
    (team) => team.id === employee.colorGroup,
  );

  const runAndRefetch = async (request: () => Promise<void>) => {
    setIsPending(true);
    try {
      await request();
    } catch {
      setIsPending(false);
      return;
    }
    if (
      queryKey.filterId != null &&
      queryKey.date != null &&
      queryKey.solutionType != null
    ) {
      await dispatch(
        getGanttChartData({
          filterId: queryKey.filterId,
          date: queryKey.date,
          solutionType: queryKey.solutionType,
        }),
      );
    }
    setIsPending(false);
  };

  // Accordion reports its initial option on mount too, so ignore the team they are already in.
  const handleSelect = (team: TeamOption) => {
    if (team.id === employee.colorGroup) return;
    runAndRefetch(() =>
      team.id === employee.homeColorGroup
        ? EmployeeSettingsService.returnShiftToTeam(employeeShiftId, dispatch)
        : EmployeeSettingsService.moveShiftToTeam(
            employeeShiftId,
            team.id,
            dispatch,
          ),
    );
  };

  return (
    <LabeledSection
      classes="employee-team-move"
      title={labels.teamMoveTitle}
      titleSize="h4"
      spacing="small"
    >
      {/* Remount on a changed team or a finished request, so a failed move snaps back to the real team */}
      <Accordion
        key={`${employee.colorGroup}-${isPending}`}
        variant="SECONDARY"
        options={teamOptions}
        labelKey="title"
        defaultText={labels.teamMoveTitle}
        displayOption={currentIndex}
        onChange={handleSelect}
        disabled={isPending}
        leadingElement={(team: TeamOption) => (
          <span
            className="inline-block h-[16px] w-[16px] shrink-0 rounded-full"
            style={{ backgroundColor: "#" + team.id }}
          />
        )}
        helperText={
          employee.homeColorGroup
            ? labels.movedFromTeamToday(teamNames[employee.homeColorGroup])
            : undefined
        }
      />
    </LabeledSection>
  );
};

export default EmployeeTeamMove;
